/** Derivations over `ChatView` / `MessageView` / `ExportView` that the UI repeats. */
import type { ChatView, ExportView, MessageView, UserView } from '@olmt/shared'
import { currentLocale, t, type MessageKey } from './i18n'
import type { en } from './strings.en'
import { formatBytes } from './time'

export function otherMembers(chat: ChatView, meId: string | null): UserView[] {
  if (!meId) return chat.members
  return chat.members.filter((member) => member.id !== meId)
}

/**
 * The people an "Add people" sheet may offer: the directory's users who are neither already in the
 * chat nor the viewer. Ordering is the directory's (api: by `displayName`), so the sheet does not
 * reshuffle between two opens. A chat that already knows a member by id keeps them out even if the
 * directory row has since been renamed.
 */
export function addableMembers(chat: ChatView, meId: string | null, directory: UserView[]): UserView[] {
  const known = new Set(chat.members.map((member) => member.id))
  return directory.filter((user) => user.id !== meId && !known.has(user.id))
}

/** A DM carries `title: null`; its label is the other member (docs/CONTRACT.md §6). */
export function chatTitle(chat: ChatView, meId: string | null): string {
  if (chat.title) return chat.title
  const others = otherMembers(chat, meId)
  if (others.length === 0) return chat.kind === 'group' ? 'Group' : 'Saved messages'
  return others.map((member) => member.displayName).join(', ')
}

export function chatAvatarUrl(chat: ChatView, meId: string | null): string | null {
  if (chat.avatarUrl) return chat.avatarUrl
  return otherMembers(chat, meId)[0]?.avatarUrl ?? null
}

/**
 * The furthest-along read cursor among the chat's *other* members (docs/CONTRACT.md §6 `reads`),
 * or null when none of them has read anything. Message ids are UUIDv7, so string order is time
 * order — the same comparison the api's `advanceLastRead` makes.
 *
 * Seeded from `ChatView.reads` at load time as well as by live `read` frames: the frames are
 * live-only, so without the seed a reload would reset every receipt to "unread".
 */
export function peerReadId(chat: ChatView, meId: string | null): string | null {
  let latest: string | null = null
  for (const read of chat.reads) {
    if (read.userId === meId) continue
    if (latest === null || read.messageId > latest) latest = read.messageId
  }
  return latest
}

/**
 * How many of the chat's *other* members are present. The number behind both the online lamp and
 * the thread header's "N online" (§10.7 item 2), so the two can never disagree.
 */
export function onlineMemberCount(
  chat: ChatView,
  meId: string | null,
  presence: Record<string, { online: boolean }>,
): number {
  return otherMembers(chat, meId).filter((member) => presence[member.id]?.online ?? member.online).length
}

export function isChatOnline(chat: ChatView, meId: string | null, presence: Record<string, { online: boolean }>): boolean {
  return onlineMemberCount(chat, meId, presence) > 0
}

/** A message kind's word in the row's preview. A key, not a sentence: `messagePreview` translates. */
const PREVIEW_BY_KIND: Record<MessageView['kind'], keyof typeof en | null> = {
  text: null,
  image: 'chat.photo',
  video: 'chat.previewVideo',
  file: 'chat.previewFile',
  voice: 'chat.previewVoice',
  call: 'chat.previewCall',
  system: null,
  // W11: a poll's body *is* the question, so the preview shows it as it stands — the options are
  // not a sentence and the row has room for one line.
  poll: null,
}

export function messagePreview(message: MessageView | null, meId: string | null): string {
  if (!message) return t('chat.noMessages')
  if (message.deletedAt) return t('chat.messageDeleted')
  const key = PREVIEW_BY_KIND[message.kind]
  const body = message.body ?? (key ? t(key) : '')
  const prefix = meId && message.authorId === meId && message.kind !== 'system' ? `${t('chat.you')}: ` : ''
  return `${prefix}${body}`.trim()
}

/**
 * The write doors on a bubble (W2). These are the only derivation: the bubble menu asks them and
 * nothing re-checks "is this mine" at a call site.
 *
 * - An **optimistic** copy (`pending`) has a `pending:` id the server has never seen; editing or
 *   deleting it would 404. It becomes actionable the moment the stored view reconciles by
 *   `clientId` (docs/CONTRACT.md §5).
 * - Edit is **text-only**: `PATCH /api/messages/:id` rewrites a `text` body and 400s on media rows.
 * - Delete covers every kind a member wrote; `system` rows are the chat's own log (the api 403s
 *   them), and a tombstone is already deleted.
 */
export function canEditMessage(message: MessageView, meId: string | null): boolean {
  return (
    !message.pending &&
    message.authorId === meId &&
    message.kind === 'text' &&
    message.deletedAt === null
  )
}

export function canDeleteMessage(message: MessageView, meId: string | null): boolean {
  return !message.pending && message.authorId === meId && message.kind !== 'system' && message.deletedAt === null
}

/**
 * W4 — the clipboard, forwarding, and what a selection may hold. Same contract as the two doors
 * above: the bubble menu and the selection bar ask these functions and nothing else.
 *
 * - **Copy is local.** The message's own words go to the clipboard, so a bare photo or voice note
 *   (`body: null`) has nothing to copy. A `call` row's body is the kind's word (`video`/`voice`)
 *   and a tombstone's was taken back, so neither is a sentence anyone wants to paste. An optimistic
 *   copy is copyable: its words are already on screen, and the clipboard needs no server id.
 * - **Forward** writes new rows in the target chats (`POST /api/messages/forward`). The api refuses
 *   `system` and `call` rows (400) and a tombstone (409), and an optimistic copy's `pending:` id is
 *   not an id the server has ever seen.
 * - **A selection takes what still has actions:** the chat's own log renders no menu at all, a
 *   tombstone has nothing left to copy, forward or delete, and a pending row is left out for the
 *   same reason `canDeleteMessage` refuses it — there is no server id to address yet.
 *
 * The blank-body rule is shared word for word with the phone's `MessageRules.kt`.
 */
export function canCopyMessage(message: MessageView): boolean {
  return (
    message.deletedAt === null &&
    message.kind !== 'system' &&
    message.kind !== 'call' &&
    (message.body?.trim().length ?? 0) > 0
  )
}

export function canForwardMessage(message: MessageView): boolean {
  return (
    !message.pending &&
    message.deletedAt === null &&
    message.kind !== 'system' &&
    message.kind !== 'call'
  )
}

export function canSelectMessage(message: MessageView): boolean {
  return !message.pending && message.deletedAt === null && message.kind !== 'system'
}

/**
 * Whether the whole selection may be deleted: every message own, a real server row, not the chat's
 * log and not already gone. The api is author-only, so one message someone else wrote closes the
 * door for the selection; an empty selection has no door to open. Deletions are not batched — the
 * caller repeats `DELETE /api/messages/:id` — which is why this is one answer about the whole list.
 */
export function canSelectForDelete(messages: MessageView[], meId: string | null): boolean {
  return messages.length > 0 && messages.every((message) => canDeleteMessage(message, meId))
}

/**
 * Newest activity first; chats without messages keep their server order at the end.
 *
 * W5 gives the sort its first key: the viewer's own pinned chats sit above every unpinned one, most
 * recently pinned first. `GET /api/chats` already sends that order, but the list re-sorts what it
 * gets, so the rule has to live here too — otherwise a pin made on this device (or a frame that
 * moved one) would be undone by the next render.
 */
export function sortChats(chats: ChatView[]): ChatView[] {
  return chats.slice().sort((a, b) => {
    if (a.pinnedAt !== null || b.pinnedAt !== null) {
      if (a.pinnedAt === null) return 1
      if (b.pinnedAt === null) return -1
      if (a.pinnedAt !== b.pinnedAt) return b.pinnedAt - a.pinnedAt
    }
    const aAt = a.lastMessageAt ?? 0
    const bAt = b.lastMessageAt ?? 0
    if (aAt !== bAt) return bAt - aAt
    return a.id < b.id ? 1 : -1
  })
}

/**
 * W5 — the two bubble-menu doors on a message pin, the same contract as the write doors above.
 *
 * - **Pin is not author-restricted:** the api's `PATCH /api/chats/:id/pinned-message` takes any
 *   message of the chat, so someone else's message can carry the bar. A tombstone is the one row it
 *   refuses (409) — the tombstone *is* the absence the pin was for.
 * - **An optimistic copy has no id to pin:** `pending:` is an id the server has never seen and the
 *   route answers 400 for an unknown id, so the item stays away until the stored row reconciles —
 *   the same rule that keeps Forward off a pending copy. A `system` row is a real stored message
 *   and the route accepts it; the bubble's own early return is why no menu is ever drawn on one.
 */
export function canPinMessage(message: MessageView): boolean {
  return !message.pending && message.deletedAt === null
}

/** W5 — whether the chat's one pinned message is this row: the menu then offers Unpin, not Pin. */
export function isPinnedMessage(message: MessageView, chat: ChatView | null | undefined): boolean {
  return chat?.pinnedMessage?.id === message.id
}

/**
 * W9 — the two reaction derivations. The chips under the bubble and the emoji strip in its menu
 * ask these and nothing else, so no component walks `userIds` itself and the two surfaces cannot
 * disagree about who reacted or about which emoji is the viewer's own.
 *
 * - A **tombstone** is closed: `PUT /api/messages/:id/reaction` answers 409 (the api's delete
 *   leaves the reaction rows where they are, but a message nobody can read is not a place to add
 *   to), so its row shows nothing.
 * - An **optimistic copy** has a `pending:` id the server has never seen and the route 404s on an
 *   unknown id — the same rule that keeps Edit, Delete, Forward and Pin off it.
 *
 * A `system` row is a real stored message and the route accepts it; the bubble's own early return
 * is why no menu is ever drawn on one — the same shape as `canPinMessage`.
 */
export function canReactTo(message: MessageView): boolean {
  return !message.pending && message.deletedAt === null
}

/**
 * The emoji *this* member used on the message, or null while they have not reacted (W9). One
 * reaction per member is what the api's `(message_id, user_id)` key says, so at most one entry can
 * carry `meId` — which is also why the frozen view needs no per-viewer flag: `userIds.includes(me)`
 * is exactly this answer. The viewer with no session has nothing of their own to mark.
 */
export function myReaction(message: MessageView, meId: string | null): string | null {
  if (!meId) return null
  return message.reactions.find((reaction) => reaction.userIds.includes(meId))?.emoji ?? null
}

/**
 * The letters a monogram shows: at most two, one per word, from the words' first code points.
 *
 * Three things this has to get right for a family whose names are mostly Arabic:
 *
 * - **One letter per word, never two code units.** `part[0]` takes half of a surrogate pair, so an
 *   emoji name drew a replacement glyph; `Array.from` walks code points.
 * - **The letters must not join.** Arabic is a joining script, so `أ` and `ع` concatenated with
 *   nothing between them shape as one word (`أع` — measured on the shipped build in the chat list),
 *   and every letter takes its contextual form instead of the isolated one a monogram wants. A
 *   zero-width non-joiner keeps each letter's isolated shape without spending a space.
 * - **`ال…` is an article, not a letter.** Every Arabic word behind it would monogram as `ا` — the
 *   same stroke for `البيت` and `الصفا` — so the article is dropped when a word is left over.
 *   `.toUpperCase()` is a no-op in Arabic and Hebrew and does the right thing in Latin.
 */
const ARABIC_ARTICLE = /^ال(?=..)/

/** Arabic and Syriac are joining scripts: two of their letters in a row shape as one word. */
const JOINING_SCRIPT = /[\p{Script=Arabic}\p{Script=Syriac}]/u
const ZWNJ = '\u200C'

export function initialsFor(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (words.length === 0) return '?'
  const letters = words
    .map((word) => Array.from(word.replace(ARABIC_ARTICLE, ''))[0]?.toUpperCase() ?? '')
    .filter((letter) => letter !== '')
  if (letters.length === 0) return '?'
  // Latin initials are already isolated by not joining; only a joining script needs the separator.
  return letters.join(letters.some((letter) => JOINING_SCRIPT.test(letter)) ? ZWNJ : '')
}

const HOUR = 3_600
const DAY = 86_400

/**
 * W13 — the timer windows the clients offer (`null` is off, the default). An hour is the api's floor
 * and a year its ceiling; these four are what a family actually picks, and both clients offer the
 * same list so a value another device set always has a name here too.
 */
export const DISAPPEARING_OPTIONS: (number | null)[] = [null, 24 * HOUR, 7 * DAY, 90 * DAY]

let unitFormatters: { locale: string; hour: Intl.NumberFormat; day: Intl.NumberFormat } | null = null

/**
 * `48 hours` / `٤٨ ساعة` — a window the dictionary does not name gets its own unit word from `Intl`,
 * so both languages read correctly and no key has to guess at a plural form. A number beside a word
 * is not a compound, so this needs no isolate (§7.9).
 */
function unitLabel(count: number, unit: 'hour' | 'day'): string {
  const locale = currentLocale()
  if (unitFormatters?.locale !== locale) {
    const options: Intl.NumberFormatOptions = { style: 'unit', unitDisplay: 'long' }
    unitFormatters = {
      locale,
      hour: new Intl.NumberFormat(locale, { ...options, unit: 'hour' }),
      day: new Intl.NumberFormat(locale, { ...options, unit: 'day' }),
    }
  }
  return unitFormatters[unit].format(count)
}

/**
 * W13 — the chat's timer in the reader's words: `Off`, `24 hours`, … The four offered windows are
 * dictionary keys (a translated sentence is a decision, not a formula); anything else the api
 * accepts — it takes any whole number of seconds from an hour to a year — falls back to the unit it
 * is a whole number of, which is why the details row and the thread's line always have something to
 * say about the value they were handed.
 */
export function disappearingWindow(seconds: number | null): string {
  if (seconds === null) return t('chat.disappearingOff')
  if (seconds === 24 * HOUR) return t('chat.disappearing24Hours')
  if (seconds === 7 * DAY) return t('chat.disappearing7Days')
  if (seconds === 90 * DAY) return t('chat.disappearing90Days')
  return seconds % DAY === 0 ? unitLabel(seconds / DAY, 'day') : unitLabel(seconds / HOUR, 'hour')
}

/** W13 — what one `messages.expired` batch leaves of a chat (see `expireMessages`). */
export type ExpiredPage = {
  /** The page without the expired rows; the input array itself when the batch changed nothing. */
  messages: MessageView[]
  /** Whether the batch removed anything here — false lets a store keep the state it already has. */
  changed: boolean
  /** The newest row left, or null when the page is empty: the chat row's preview becomes this. */
  newest: MessageView | null
}

/**
 * W13 — one `messages.expired` batch against the page a client holds.
 *
 * The frame names ids and carries no tombstones, because a message past its chat's timer is *gone*:
 * the row, its media row and the media's files went together (docs/CONTRACT.md §4, §8). So the rows
 * are dropped rather than marked the way `message.deleted` leaves a tombstone, and the page keeps
 * its order. An id this page does not hold is ignored — it belongs to a page that was never loaded —
 * which is the same rule `applyEdited` and its neighbours follow, and it is why the input array is
 * handed back untouched (the identity a store's selectors compare) whenever nothing here matched.
 *
 * `newest` is what the chat row's `lastMessage` becomes when the batch took the message standing
 * there: it is the row's preview and the list's sort key, and a preview naming a message that no
 * longer exists is a leak of the very thing the timer was for.
 */
export function expireMessages(messages: MessageView[], messageIds: ReadonlySet<string>): ExpiredPage {
  const survivors = messages.filter((message) => !messageIds.has(message.id))
  const changed = survivors.length !== messages.length
  const kept = changed ? survivors : messages
  return { messages: kept, changed, newest: kept[kept.length - 1] ?? null }
}

/** `{count} message` / `{count} messages` — an exact one is its own word, everything else the plural. */
function countedLabel(count: number, one: MessageKey, other: MessageKey): string {
  return t(count === 1 ? one : other, { count })
}

/**
 * W14 — the export's ready line in the reader's words: `Exported 12 messages and 3 media files
 * (4.1 MB).` The counts are *worded* rather than interpolated — a one-message archive says
 * `1 message`, not `1 messages` — and that choice is made here, where a test can pin it, never by
 * string surgery at the call site. The size is `formatBytes`, whose isolate (§7.9) keeps a decimal
 * like `1.5 MB` one object inside an Arabic sentence instead of two number runs around a point.
 */
export function exportSummary(archive: Pick<ExportView, 'bytes' | 'messageCount' | 'mediaCount'>): string {
  return t('chat.exportReady', {
    messages: countedLabel(archive.messageCount, 'chat.exportMessagesOne', 'chat.exportMessagesOther'),
    media: countedLabel(archive.mediaCount, 'chat.exportMediaOne', 'chat.exportMediaOther'),
    size: formatBytes(archive.bytes),
  })
}
